// ════════════════════════════════════════════════════════════
//  online/lockstep.js — one fight, the same on every screen
// ════════════════════════════════════════════════════════════
//  caP'n: "You can watch the battles right now in Shatterproof, I want
//  to be able to watch the same battles with a friend."
//
//  THE TRICK: the engine is seeded and deterministic, so a fight is
//  fully described by   seed + teams + start time + every command a
//  human gave, stamped with the tick it happened on.
//  Nobody streams positions. Every screen runs the SAME engine on the
//  SAME inputs and gets the same fight. An all-NPC fight needs nothing
//  sent at all; a late joiner just fast-forwards (a whole fight sims in
//  about a tenth of a second).
//
//  ── WHAT A HUMAN'S SCREEN SENDS ─────────────────────────────
//   · PACKETS (pushes, one ordered list per side):
//       { a, b, c?:[[tick, cmd]…] } = "ALL my commands for ticks a..b"
//     sent whenever there are commands, and empty as a heartbeat (every
//     flush in PvP, every IDLE_PACKET_MS against NPCs).
//   · COVER (one node, about once a second):
//       cover/{side} = { b, ts, hash? } = "alive at server time ts,
//       promised up to b" (+ a desync fingerprint). Only takeovers and the
//       desync check read it — the LOCK reads packets, because one list
//       keeps one order and a promise can never outrun its commands.
//  A screen may only simulate tick k once every other human side's
//  packets cover k — that's the lock. Against NPCs the player's own
//  screen never waits; in PvP each side stamps INPUT_DELAY ahead so the
//  other's packet is usually there first. Watchers trail by WATCH_DELAY.
//
//  ── THE TAKEOVER (a closed tab can't freeze everyone) ───────
//  takeover lives INSIDE cover/{side}, written by a transaction that
//  only commits if the server's own cover is STALL_TAKEOVER seconds old,
//  and it is always the SERVER's cover.b + 1 — never a tick one screen
//  happened to hold. From that tick the AI presses that side's buttons
//  ("auto on" at exactly that tick, on every screen).
//
//  ── THE NO-SHOW (caP'n 2026-10-16: "if they have an ai partner yes have
//  them … if not forfeit") ──────────────────────────────────────
//  spec.noShow = { side: "ai" | "forfeit" }. A player who NEVER showed up
//  (no cover at all, NO_SHOW_GRACE after the bell): "ai" → the takeover as
//  above; "forfeit" → cover/{side}.forfeit is set (same transaction, same
//  server-judged clock) and the other side wins, why "forfeit". Both sides
//  forfeiting: the fight's seed flips the coin. A player who showed and then
//  left is never forfeited — the AI takes over, as always.
//
//  ── SELF-HEALING (the rebuild) ──────────────────────────────
//  Every command ever received is kept in a log. If anything arrives
//  for a tick this screen has ALREADY simulated (a packet that lost a
//  race with its cover, a takeover at a tick we're past), the screen
//  rebuilds the fight from the seed with the full log (≤ 0.2 s) and
//  carries on. So a screen can be late, but it can't stay wrong.
//
//  ── LAWS ────────────────────────────────────────────────────
//  · The battle is only ever advanced by stepFixed(), one 1/120 s tick
//    at a time. Never step() here (frame slicing differs per screen).
//  · Every command that changes the fight goes through send(), aim
//    included, ROUNDED before it is applied anywhere (the same number
//    on every screen, and smaller packets).
//  · Results: in a fight with humans only the humans write the result;
//    anyone may write an all-NPC result, and a REPLAY may write any
//    result once the fight's clock has certainly run out. First write
//    wins; a screen whose own ending disagrees flags itself desynced.
//  · Same engine = same fight ON THE SAME JS ENGINE. Chrome/Edge/Brave
//    (V8) agree bit for bit; Firefox/Safari may round Math.sin()
//    differently. The cover hash catches it and says so.
// ════════════════════════════════════════════════════════════
import { createBattle, command, stepFixed, stateHash, TICK, CALL_TIME } from "../sim/battle.js";
import { STALL_TAKEOVER, NO_SHOW_GRACE } from "./dials.js";

export const INPUT_DELAY = 0.25;     // ⟵ DIAL (s) — PvP only: how far ahead a command is stamped
export const WATCH_DELAY = 1.0;      // ⟵ DIAL (s) — watchers trail a fight that has humans in it
export const FLUSH_MS = 66;          // ⟵ DIAL — how often a player's screen sends
export const IDLE_PACKET_MS = 400;   // ⟵ DIAL — vs NPC, an empty "still here" packet this often (PvP: every flush)
export const COVER_MS = 1000;        // ⟵ DIAL — the cover node (for takeovers + the desync hash)
export { STALL_TAKEOVER };
export const HASH_EVERY = 5;         // ⟵ DIAL (s of fight) — desync fingerprint cadence
const MAX_TICKS_PER_FRAME = 60000;   // a whole 12-minute fight is ~86k ticks; catch-up takes ≤2 frames
const AIM_EPS = 0.15;
const r2 = (v) => Math.round(v * 100) / 100;

const T = (sec) => Math.round(sec / TICK);
const HASH_TICKS = T(HASH_EVERY);

/**
 * spec: { seed, teams, tomes, names, humans:{0:uid|null,1:uid|null}, startAt }
 * me: my uid (or null) · now: () => server ms · path: the match node · net: online/net.js
 */
const REAL_TIMERS = { setInterval: (f, ms) => setInterval(f, ms), clearInterval: (h) => clearInterval(h) };
export function createDriver({ spec, me, now, path, onResult, net, timers = REAL_TIMERS, headless = false }) {
  const humans = [spec.humans?.[0] || null, spec.humans?.[1] || null];
  const fresh = () => {
    const nb = createBattle({
      teams: spec.teams, tomes: spec.tomes || [null, null], seed: spec.seed,
      auto: [!humans[0], !humans[1]], names: spec.names || ["Blue", "Red"], callTime: spec.callTime || CALL_TIME,
      styles: spec.styles || [null, null],   // the League's playbooks (a spec without them: the default AI)
    });
    nb.tick = 0;
    return nb;
  };
  const b = fresh();
  const mySide = me && humans[0] === me ? 0 : me && humans[1] === me ? 1 : null;
  const pvp = !!(humans[0] && humans[1]);
  const delay = pvp ? T(INPUT_DELAY) : 0;
  const watchLag = mySide == null && (humans[0] || humans[1]) ? T(WATCH_DELAY) : 0;

  // per side: covered = last tick we hold every command for; log = every command, forever
  const sides = [0, 1].map((s) => ({
    human: !!humans[s], covered: humans[s] ? -1 : Infinity, log: new Map(),
    takeoverAt: null, hashes: new Map(), coverTs: null,
  }));
  const offs = [];
  const myHashes = new Map();
  const st = { stopped: false, desync: false, stalled: false, result: null, takenOver: [false, false], lostControl: false, rebuilds: 0, waitingOn: null };
  let dirtyFrom = Infinity;        // the earliest already-simulated tick that just learned something new

  const logCmd = (s, k, cmd) => {
    const S = sides[s];
    if (S.takeoverAt != null && k >= S.takeoverAt) return;     // the AI owns those ticks
    if (!S.log.has(k)) S.log.set(k, []);
    S.log.get(k).push(cmd);
    if (k < b.tick) dirtyFrom = Math.min(dirtyFrom, k);
  };
  const autoAt = (s) => sides[s].takeoverAt;

  // ── listening ──────────────────────────────────────────────
  for (const s of [0, 1]) {
    if (!humans[s]) continue;
    if (s !== mySide) {
      offs.push(net.onChild(`${path}/inputs/${s}`, (p) => {
        if (!p) return;
        for (const [k, cmd] of p.c || []) logCmd(s, k, cmd);
        if (p.b > sides[s].covered) sides[s].covered = p.b;
      }));
    }
    offs.push(net.onValue(`${path}/cover/${s}`, (v) => {
      if (!v) return;
      const S = sides[s];
      if (typeof v.ts === "number") S.coverTs = v.ts;
      if (v.hash) { S.hashes.set(v.hash[0], v.hash[1]); const mine = myHashes.get(v.hash[0]); if (mine && mine !== v.hash[1]) st.desync = true; }
      if (v.takeover != null) applyTakeover(s, v.takeover);
      if (v.forfeit) applyForfeit(s);
    }));
  }
  offs.push(net.onValue(`${path}/result`, (v) => {
    if (!v) return;
    st.result = v;
    if (b.over && b.winner !== v.winner) st.desync = true;
    if (onResult) onResult(v);
  }));

  // THE TAKEOVER: from tick v the AI presses that side's buttons. Commands we
  // hold for ticks before v still count (they may arrive after the takeover —
  // a packet and a cover travel separately); everything at or past v is dropped.
  function applyTakeover(s, v) {
    const S = sides[s];
    if (v == null || S.takeoverAt != null) return;
    S.takeoverAt = v;
    for (const k of [...S.log.keys()]) if (k >= v) S.log.delete(k);
    if (v < b.tick) dirtyFrom = Math.min(dirtyFrom, v);
    st.takenOver[s] = true;
    if (s === mySide) st.lostControl = true;   // my tab slept too long; the AI has my side now
  }
  // THE FORFEIT: a no-show with no partner loses. Every screen ends the fight the
  // moment it learns of it; the result says so, and only one is ever written.
  function applyForfeit(s) {
    if (st.forfeit != null) return;
    st.forfeit = s;
    const res = forfeitResult(spec, s, sides[1 - s].coverTs != null);
    if (!b.over) { b.over = true; b.winner = res.winner; b.events.push({ type: "end", winner: res.winner, why: "forfeit", seq: (b.seq = (b.seq || 0) + 1), t: b.t }); }
    if (!headless) Promise.resolve(net.txn(`${path}/result`, (cur) => cur || { ...res, writer: me || "watcher" })).catch(() => {});
  }
  // a side is "spoken for" at tick k if we hold its packets that far, or the AI has it by then
  const coveredAt = (s, k) => {
    const S = sides[s];
    if (S.covered >= k) return true;
    return S.takeoverAt != null && S.covered >= S.takeoverAt - 1;
  };

  // ── producing (only if I'm one of the humans) ──────────────
  const out = { from: 0, cmds: [], lastAim: null, pendingAim: null, lastSteer: undefined, hash: null, lastPush: -1e12, lastCover: -1e12 };
  const stampTick = () => Math.max(b.tick + delay, out.from);   // never at or before a tick already promised
  function queueLocal(cmd) {
    const k = stampTick();
    out.cmds.push([k, cmd]);
    logCmd(mySide, k, cmd);
  }
  function flush() {
    if (mySide == null || st.stopped) return;
    if (b.over || st.lostControl) return;           // nothing left to promise
    if (out.pendingAim) { queueLocal({ type: "aim", ...out.pendingAim }); out.pendingAim = null; }
    // promise everything up to the live tick + delay — from TIME, not from my sim:
    // a backgrounded tab keeps its promises and the opponent never waits on it
    const live = Math.max(b.tick, tickAt(now())) + delay;
    const to = Math.max(out.from - 1, live - 1);
    // ⚑ the LOCK reads coverage from PACKETS only: one path keeps one order, so a
    // screen can never hold a promise for a tick whose commands are still in flight
    const t0 = now();
    if (out.cmds.length || (to >= out.from && (pvp || t0 - out.lastPush >= IDLE_PACKET_MS))) {
      const pkt = { a: out.from, b: to };
      if (out.cmds.length) pkt.c = out.cmds;
      Promise.resolve(net.push(`${path}/inputs/${mySide}`, pkt)).catch(() => {});
      out.cmds = []; out.from = to + 1; out.lastPush = t0;
    }
    // the cover: "still alive, promised up to here" — what a takeover is judged on
    if (out.hash || t0 - out.lastCover >= COVER_MS) {
      const cover = { b: out.from - 1, ts: net.TS ? net.TS() : t0 };
      if (out.hash) { cover.hash = out.hash; out.hash = null; }
      Promise.resolve(net.update(`${path}/cover/${mySide}`, cover)).catch(() => {});
      out.lastCover = t0;
    }
  }
  const flusher = mySide != null && !headless ? timers.setInterval(flush, FLUSH_MS) : null;

  /** The ONE door for a local player's command (Hud + aim). */
  function send(cmd) {
    if (mySide == null || st.stopped || st.lostControl || b.over) return false;
    if (cmd.type === "aim") {
      const a = { x: r2(cmd.x), z: r2(cmd.z) }, l = out.lastAim;
      if (l && Math.abs(l.x - a.x) < AIM_EPS && Math.abs(l.z - a.z) < AIM_EPS) return true;
      out.lastAim = a; out.pendingAim = a;
      return true;
    }
    if (cmd.type === "steer") {
      cmd = cmd.x == null ? { type: "steer", x: null } : { type: "steer", x: r2(cmd.x), z: r2(cmd.z) };
      const key = cmd.x == null ? "x" : `${cmd.x},${cmd.z}`;
      if (key === out.lastSteer) return true;
      out.lastSteer = key;
    }
    if (out.pendingAim) { queueLocal({ type: "aim", ...out.pendingAim }); out.pendingAim = null; }
    queueLocal(cmd);
    return true;   // the engine decides at its tick; the HUD reads the outcome
  }

  // ── the clock ──────────────────────────────────────────────
  const tickAt = (ms) => Math.floor((ms - spec.startAt) / 1000 / TICK);
  function applyTick(bb, k) {
    for (const s of [0, 1]) {
      if (autoAt(s) === k) command(bb, s, { type: "auto", on: true });
      const q = sides[s].log.get(k);
      if (q) for (const cmd of q) command(bb, s, cmd);
    }
    stepFixed(bb);
    bb.tick++;
  }
  // rebuild from the seed with everything we now know, up to where we were
  function rebuild() {
    const until = b.tick;
    const nb = fresh();
    // what we believed before was built on a missing command: judge afresh
    st.desync = false; myHashes.clear();
    while (nb.tick < until) {
      applyTick(nb, nb.tick);
      if (nb.tick % HASH_TICKS === 0) checkHash(nb);
    }
    const keep = b.driver;
    for (const k of Object.keys(b)) delete b[k];
    Object.assign(b, nb, { driver: keep });
    st.rebuilds++;
    dirtyFrom = Infinity;
  }
  function checkHash(bb) {
    const h = stateHash(bb);
    myHashes.set(bb.tick, h);
    for (const s of [0, 1]) { const theirs = sides[s].hashes.get(bb.tick); if (theirs && theirs !== h) st.desync = true; }
    return h;
  }
  function frame() {
    if (st.stopped) return;
    if (dirtyFrom < b.tick) rebuild();
    const t = now();
    const target = tickAt(t) - watchLag;
    let n = 0;
    st.stalled = false; st.waitingOn = null;
    while (b.tick < target && n < MAX_TICKS_PER_FRAME && !b.over) {   // the bell is the bell: nobody ticks past it
      const k = b.tick;
      // the lock: every OTHER human side must have spoken for tick k
      let wait = false;
      for (const s of [0, 1]) if (s !== mySide && sides[s].human && !coveredAt(s, k)) {
        // HEADLESS (a replay after the fact): a side whose packets ran out is
        // the AI's from the next tick — exactly what the live takeover writes
        if (headless) applyTakeover(s, sides[s].covered + 1); else { wait = true; st.waitingOn = s; }
      }
      if (wait) { st.stalled = true; break; }
      applyTick(b, k);
      n++;
      if (b.tick % HASH_TICKS === 0) { const h = checkHash(b); if (mySide != null) out.hash = [b.tick, h]; }
      if (b.over) { claimResult(); break; }
    }
    if (st.stalled && !headless) checkTakeover(t);
  }

  function claimResult() {
    if (st.resultClaimed || headless) return;
    // a fight with humans in it: only they write the result (a replay can, later)
    if ((humans[0] || humans[1]) && mySide == null) return;
    st.resultClaimed = true;
    const endEv = b.events.find((e) => e.type === "end");
    const res = { winner: b.winner, why: endEv ? endEv.why : "ruined", call: (endEv && endEv.by) || null, tick: b.tick, hash: stateHash(b), writer: me || "watcher", ...scoreOf(b) };
    Promise.resolve(net.txn(`${path}/result`, (cur) => cur || res))
      .then((w) => { if (w && w.value && w.value.winner !== b.winner) st.desync = true; })
      .catch(() => { st.resultClaimed = false; });   // offline at the bell: try again next frame
  }

  function checkTakeover(t) {
    const s = st.waitingOn;
    if (s == null) return;
    const S = sides[s];
    // a player who never showed up has no cover at all: the clock starts at the bell
    const since = S.coverTs != null ? S.coverTs : spec.startAt;
    const wait = S.coverTs != null ? STALL_TAKEOVER : NO_SHOW_GRACE;   // never showed → a longer grace to arrive
    if (S.takeoverAt != null || (t - since) / 1000 < wait) return;
    if (S.pendingTakeover && t - S.pendingTakeover < 3000) return;
    S.pendingTakeover = t;
    // judged on the SERVER's copy of their cover, inside the transaction
    Promise.resolve(net.txn(`${path}/cover/${s}`, (cur) => {
      if (cur && (cur.takeover != null || cur.forfeit)) return undefined;
      const showed = cur && typeof cur.ts === "number";
      const since = showed ? cur.ts : spec.startAt;
      if ((now() - since) / 1000 < (showed ? STALL_TAKEOVER : NO_SHOW_GRACE)) return undefined;
      // never showed, and nobody to fight for them: a forfeit (caP'n's rule)
      if (!showed && spec.noShow && spec.noShow[s] === "forfeit") return { ...(cur || {}), forfeit: true };
      return { ...(cur || {}), takeover: (cur && typeof cur.b === "number" ? cur.b : -1) + 1 };
    })).catch(() => {});
  }

  /** Leaving mid-fight: hand my side to the AI from my next unpromised tick, and stop. */
  function leave() {
    if (mySide != null && !b.over && !st.stopped && !st.lostControl) {
      flush();
      const at = out.from;
      Promise.resolve(net.txn(`${path}/cover/${mySide}`, (cur) => (cur && cur.takeover != null ? undefined : { ...(cur || {}), b: at - 1, takeover: at }))).catch(() => {});
    }
    stop();
  }
  function stop() {
    if (st.stopped) return;
    st.stopped = true;
    if (flusher) timers.clearInterval(flusher);
    offs.forEach((o) => { try { o(); } catch { /* gone */ } });
  }

  b.driver = { frame, send, leave, stop, mySide, state: st, spec, path };   // path: the match node (callouts read it; LYRAEI PLAYS)
  return b;
}

// ════════════════════════════════════════════════════════════
//  replayMatch — finish a fight nobody stayed to watch
// ════════════════════════════════════════════════════════════
//  Reads the match (spec + every packet + covers/takeovers) once and runs
//  it to the end headless. Same engine, same inputs → the same result the
//  screens saw. Used to settle bets and to run THE HOURLY when nobody is
//  around. Resolves { winner, why, tick } or null if it can't be over yet.
export async function replayMatch(net, path, nowMs) {
  const m = await net.get(path);
  if (!m || !m.spec || m.spec.seed == null) return null;
  const inputs = m.inputs || {}, cover = m.cover || {};
  const mem = {
    onChild: (p, cb) => { const side = p.split("/").pop(); for (const v of Object.values(inputs[side] || {})) cb(v); return () => {}; },
    onValue: (p, cb) => {
      const parts = p.split("/"); const side = parts.pop(), key = parts.pop();
      if (key === "cover") { const c = cover[side]; cb(c ? { ...c, hash: null } : null); } else cb(null);
      return () => {};
    },
    push: () => {}, update: () => {}, txn: async () => ({ committed: false }),
  };
  // a no-show with no partner, by the record: forfeited (the live screens may never have written it)
  for (const s of [0, 1]) {
    const human = m.spec.humans && m.spec.humans[s];
    const showed = (x) => !!(cover[x] && typeof cover[x].ts === "number") || !!(inputs[x] && Object.keys(inputs[x]).length);
    if (human && ((cover[s] && cover[s].forfeit) || (!showed(s) && m.spec.noShow && m.spec.noShow[s] === "forfeit"))) return forfeitResult(m.spec, s, showed(1 - s));
  }
  const b = createDriver({ spec: m.spec, me: null, now: () => nowMs, path, net: mem, headless: true });
  for (let i = 0; i < 40 && !b.over; i++) b.driver.frame();
  b.driver.stop();
  if (!b.over) return null;
  const endEv = b.events.find((e) => e.type === "end");
  return { winner: b.winner, why: endEv ? endEv.why : "ruined", call: (endEv && endEv.by) || null, tick: b.tick, ...scoreOf(b) };
}
/** How each side finished: beings still standing, Anima left on them (the Circuit's ledger reads these). */
export function scoreOf(b) {
  const up = [0, 0], an = [0, 0];
  b.sides.forEach((s, i) => { for (const r of s.roster) if (!r.ruined) { up[i]++; an[i] += Math.max(0, Math.round(r.anima || 0)); } });
  return { up, an };
}

/**
 * The result of a match, however it ended: the stored one if a screen saw
 * it, else (once its clock has certainly run out) a headless replay, written
 * back so nobody replays it twice. null = still going. { void: true } when
 * the match was called off (spec.void) — bets on it refund.
 */
export async function matchResult(net, path, nowMs) {
  const res = await net.get(`${path}/result`);
  if (res) return res;
  const spec = await net.get(`${path}/spec`);
  if (!spec) return null;
  if (spec.void) return { winner: null, void: true };
  // set up and never started: called off after a while (bets refund)
  if (spec.seed == null) return spec.createdAt && nowMs - spec.createdAt > 2 * 3600 * 1000 ? { winner: null, void: true } : null;
  if (nowMs < spec.startAt + ((spec.callTime || CALL_TIME) + 20) * 1000) return null;
  const r = await replayMatch(net, path, nowMs);
  if (!r) return null;
  const out = { ...r, writer: "replay" };
  const w = await net.txn(`${path}/result`, (cur) => cur || out);
  return w.value || out;
}

/**
 * The result of side `s` forfeiting. otherShowed = has the OTHER side ever shown up?
 * Both sides absent with nobody to fight for either → the fight's seed flips the coin.
 */
export function forfeitResult(spec, s, otherShowed) {
  const other = 1 - s;
  const bothOut = !otherShowed && !!(spec.humans && spec.humans[other]) && !!(spec.noShow && spec.noShow[other] === "forfeit");
  return { winner: bothOut ? ((spec.seed >>> 0) & 1) : other, why: "forfeit", call: bothOut ? "coin" : null, tick: 0 };
}

